import { useState } from 'react';

import { ApiError, api, type Domain, type Me } from '../lib/api';
import { Alert, Badge, Card, CopyButton, Field, PageHead, VerificationBadge, formatTime, useLoader } from '../components/ui';

export function Domains({ me }: { me: Me }) {
  const { data, error, loading, reload } = useLoader(() => api.domains.list());
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [mailType, setMailType] = useState('transactional');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [records, setRecords] = useState<Domain | null>(null);
  // Checking verification is a round trip to SES that usually changes nothing,
  // because DNS takes time to propagate. Without these two, a successful check
  // that came back "still pending" was indistinguishable from a dead button --
  // the screen redrew exactly what was already there.
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<string | null>(null);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setNameError(null);

    // A hostname, not a URL and not a bare word. Caught here so the correction
    // lands on the field; the server validates it properly regardless.
    const candidate = name.trim().toLowerCase();
    if (/^https?:\/\//.test(candidate)) {
      setNameError('Enter just the domain, without https://');
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(candidate)) {
      setNameError('That does not look like a domain — try mail.yourcompany.com');
      return;
    }

    setBusy(true);
    try {
      const domain = await api.domains.create(candidate, mailType);
      // The DNS records are the whole point of this response, so the created
      // domain is held open rather than collapsing back to the list.
      setRecords(domain);
      setAdding(false);
      setName('');
      reload();
    } catch (err) {
      // `domain_limit_reached` stays at form level on purpose: nothing is wrong
      // with what was typed, so an error against the field would be misleading —
      // the answer is to upgrade, not to edit the name.
      if (err instanceof ApiError && err.code !== 'domain_limit_reached') {
        setNameError(err.message);
      } else {
        setFormError(err instanceof Error ? err.message : 'Could not add domain');
      }
    } finally {
      setBusy(false);
    }
  }

  async function refreshOne(domainId: string) {
    setChecking(true);
    setCheckResult(null);
    setFormError(null);
    try {
      // GET re-polls SES server-side, so this is the verification check.
      const domain = await api.domains.get(domainId);
      setRecords(domain);
      reload();

      const dkim = domain.verification_status;
      const mailFrom = domain.mail_from_status;
      const at = new Date().toLocaleTimeString();

      if (dkim === 'verified' && mailFrom === 'verified') {
        setCheckResult(`Verified. You can send from ${domain.domain_name} now.`);
      } else if (dkim === 'failed' || mailFrom === 'failed') {
        // SES gives up after 72 hours. Saying so matters: otherwise somebody
        // keeps clicking a button that can no longer succeed.
        setCheckResult(
          `Checked at ${at}: the provider marked this failed, which it does after ` +
            `72 hours without finding the records. Fix the DNS, then remove and ` +
            `re-add the domain to start a fresh check.`,
        );
      } else {
        // The common case, and the one that looked broken.
        setCheckResult(
          `Checked at ${at}: DKIM ${dkim}, MAIL FROM ${mailFrom}. Nothing has ` +
            `changed yet — DNS usually takes 15 minutes to a few hours to ` +
            `propagate, and this re-checks with the provider on every click.`,
        );
      }
    } catch (err) {
      // Previously `void`-ed away: the call could fail and the button would look
      // like it had done nothing at all.
      setFormError(
        err instanceof Error ? err.message : 'Could not check verification',
      );
    } finally {
      setChecking(false);
    }
  }

  return (
    <>
      <PageHead
        title="Domains"
        subtitle="Send from your own domain, or from the platform subdomain with no setup at all."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : 'Add domain'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {adding && (
        <Card title="Add a sending domain">
          {formError && <Alert kind="error">{formError}</Alert>}
          <form onSubmit={create}>
            <Field
              id="domain"
              label="Domain"
              error={nameError}
              hint="A subdomain is recommended — it keeps your sending reputation separate from your main domain."
            >
              {(props) => (
                <input
                  {...props}
                  required
                  placeholder="mail.yourcompany.com"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (nameError) setNameError(null);
                  }}
                />
              )}
            </Field>
            <Field
              id="mail-type"
              label="Mail type"
              hint="Marketing domains are held to a stricter complaint threshold."
            >
              {(props) => (
                <select
                  {...props}
                  value={mailType}
                  onChange={(event) => setMailType(event.target.value)}
                >
                  <option value="transactional">Transactional</option>
                  <option value="marketing">Marketing</option>
                  <option value="both">Both</option>
                </select>
              )}
            </Field>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create and show DNS records'}
            </button>
          </form>
        </Card>
      )}

      {records && (
        <Card
          title={`DNS records for ${records.domain_name}`}
          actions={
            <div className="row">
              <button
                type="button"
                className="btn btn-sm"
                disabled={checking}
                onClick={() => void refreshOne(records.id)}
              >
                {checking ? 'Checking…' : 'Check verification'}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setRecords(null);
                  setCheckResult(null);
                }}
              >
                Close
              </button>
            </div>
          }
          tight
        >
          <div style={{ padding: '14px 16px 0' }}>
            {checkResult && (
              <Alert
                kind={checkResult.startsWith('Verified') ? 'ok' : 'info'}
                // Announced, because the whole failure here was a change nobody
                // could perceive.
                role="status"
              >
                {checkResult}
              </Alert>
            )}
            <Alert kind="info">
              Publish <strong>all</strong> of these. Adding only the DKIM records
              verifies signing and then fails at send time, because the MAIL FROM MX
              record is missing.
            </Alert>
            <div className="row" style={{ marginBottom: 12 }}>
              <span>
                DKIM <VerificationBadge status={records.verification_status} />
              </span>
              <span>
                MAIL FROM <VerificationBadge status={records.mail_from_status} />
              </span>
            </div>
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <CopyButton
              label="Copy all as a zone file"
              // One paste for a provider that accepts BIND format, instead of
              // six trips through a web form. The trailing dot and the quoting
              // are what those importers expect.
              value={(records.records ?? [])
                .map((record) => {
                  const name = record.name.endsWith('.')
                    ? record.name
                    : `${record.name}.`;
                  const value =
                    record.type === 'TXT' ? `"${record.value}"` : record.value;
                  const priority =
                    record.priority !== null ? `${record.priority} ` : '';
                  return `${name}\t3600\tIN\t${record.type}\t${priority}${value}`;
                })
                .join('\n')}
            />
            <span className="hint">
              Or copy each value individually from the table below.
            </span>
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Value</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                {(records.records ?? []).map((record) => (
                  <tr key={`${record.type}-${record.name}-${record.value}`}>
                    <td>
                      <Badge kind="muted">{record.type}</Badge>
                    </td>
                    <td>
                      {/* Wrapped, not truncated. A DKIM value is ~100 characters
                          and has to be transcribed exactly; hiding the middle of
                          it behind an ellipsis and a hover tooltip made the one
                          thing this table exists for impossible to read. */}
                      <div className="dns-cell">
                        <code className="dns-value">{record.name}</code>
                        <CopyButton
                          value={record.name}
                          title={`Copy the ${record.type} record name`}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="dns-cell">
                        <code className="dns-value">{record.value}</code>
                        <CopyButton
                          value={record.value}
                          title={`Copy the ${record.type} record value`}
                        />
                      </div>
                      {record.priority !== null && (
                        <div className="hint" style={{ marginTop: 4 }}>
                          {/* Outside the copied value on purpose: priority is a
                              separate field in every DNS provider's form, and
                              pasting "10 mx.example" into the value box fails. */}
                          priority {record.priority}
                        </div>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{record.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card tight title="Your domains">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Domain</th>
                <th>DKIM</th>
                <th>MAIL FROM</th>
                <th>Type</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((domain) => (
                <tr key={domain.id}>
                  <td className="mono">
                    {domain.domain_name}
                    {domain.is_platform_subdomain && (
                      <>
                        {' '}
                        <Badge kind="info">platform</Badge>
                      </>
                    )}
                  </td>
                  <td>
                    <VerificationBadge status={domain.verification_status} />
                  </td>
                  <td>
                    <VerificationBadge status={domain.mail_from_status} />
                  </td>
                  <td>{domain.mail_type}</td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {formatTime(domain.created_at)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!domain.is_platform_subdomain && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => void refreshOne(domain.id)}
                      >
                        Records
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(data ?? []).length === 0 && (
          <div className="empty">{loading ? 'Loading…' : 'No domains yet.'}</div>
        )}
      </Card>

      <Card>
        <div className="tile-label">Zero-setup sending</div>
        <p className="subtitle" style={{ marginTop: 6 }}>
          <code>{me.tenant.sending_domain}</code> is verified already — it inherits DKIM,
          SPF, DMARC and MAIL FROM from the platform domain, so you can send from it
          today while your own domain propagates.
        </p>
        <div className="row" style={{ marginTop: 8 }}>
          {/* The address, not the domain: what goes in a `from` field is
              something@domain, and copying the bare domain means editing it
              anyway. */}
          <CopyButton
            label="Copy a from address"
            value={`hello@${me.tenant.sending_domain}`}
          />
          <span className="hint">
            <code>hello@{me.tenant.sending_domain}</code> — any local part works.
          </span>
        </div>
      </Card>
    </>
  );
}
